import { TinyColor } from "@ctrl/tinycolor";
import {
  getActiveFunders,
  getActiveProposals,
  getActiveRecommenders,
} from "@engine/round/accessors";
import { Node, RoundAggregate } from "@engine/round/aggregate";
import { sortByProperty } from "@lib/utils/sortByProperty";
import { interpolateCool, interpolateViridis, interpolateWarm } from "d3";

class NodeColorsSingleton {
  aggregateCacheVersion: number = -1;
  nodeIdToColorCache: { [nodeId: string]: string } = {};

  private lookupColor(round: RoundAggregate, node: Node): string {
    if (node.id === round.holdProposalId || node.shortname === "Hold") {
      return "#B0B0B0";
    }

    if (round.version !== this.aggregateCacheVersion) {
      this.updateCache(round);
    }

    return this.nodeIdToColorCache[node.id];
  }

  updateCache(round: RoundAggregate) {
    this.nodeIdToColorCache = {};

    const proposals = getActiveProposals(round)
      .filter((proposal) => proposal.id !== round.holdProposalId)
      .sort(sortByProperty("shortname"));
    for (let i = 0; i < proposals.length; i++) {
      const proposal = proposals[i];
      this.nodeIdToColorCache[proposal.id] = interpolateWarm(
        i / proposals.length,
      );
    }
    const recommenders = getActiveRecommenders(round).sort(
      sortByProperty("shortname"),
    );
    for (let i = 0; i < recommenders.length; i++) {
      const recommender = recommenders[i];
      this.nodeIdToColorCache[recommender.id] = interpolateCool(
        i / recommenders.length,
      );
    }
    const funders = getActiveFunders(round).sort(sortByProperty("shortname"));
    for (let i = 0; i < funders.length; i++) {
      const funder = funders[i];
      this.nodeIdToColorCache[funder.id] = interpolateViridis(
        i / funders.length,
      );
    }
  }

  getColor(round: RoundAggregate, node: Node, darken: boolean = false): string {
    const color = this.lookupColor(round, node);

    if (!darken) {
      return color;
    }

    return new TinyColor(color).darken(10).toHslString();
  }
}

export const NodeColors = new NodeColorsSingleton();
